// Children & Family Yoga Frome - Service Page
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Users, Heart, Sparkles } from "lucide-react";
import Navigation from "../../components/shared/Navigation";
import Footer from "../../components/shared/Footer";
import { usePageMeta, generateServiceSchema, SchemaScript } from "../../components/shared/SEOHelper";

const ChildrenFamilyYogaFrome = () => {
  usePageMeta({
    title: "Children & Family Yoga Frome | Kids Yoga Somerset",
    description: "Children and family yoga classes in Frome, Somerset. Playful, engaging and supportive yoga for children, teenagers and families with Annelie Lily.",
    canonical: "https://annelielily.com/yoga-classes/children-family-yoga"
  });

  const serviceSchema = generateServiceSchema({
    name: "Children & Family Yoga in Frome",
    description: "Yoga classes for children, teenagers and families in Frome, Somerset. Playful, engaging and supportive sessions.",
    serviceType: "Children's Yoga Class",
    url: "https://annelielily.com/yoga-classes/children-family-yoga"
  });

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={serviceSchema} />
      <Navigation />
      
      {/* Hero */}
      <section className="pt-28 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
              Children & Family Yoga in Frome
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">
              Children & Family Yoga
            </h1>
            <div className="flex flex-wrap justify-center gap-3 mb-6">
              <span className="bg-gold/10 text-gold px-4 py-1 rounded-full text-sm font-medium">Gentle</span>
              <span className="bg-gold/10 text-gold px-4 py-1 rounded-full text-sm font-medium">Fun</span>
              <span className="bg-gold/10 text-gold px-4 py-1 rounded-full text-sm font-medium">Nurturing</span>
            </div>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-8">
              Annelie offers yoga classes for children, teenagers and families. These classes are playful, engaging and supportive, helping young people develop body awareness, confidence and emotional balance.
            </p>
            <Link to="/contact" className="btn-primary" data-testid="hero-cta">
              Enquire About Classes <ArrowRight className="w-4 h-4 inline ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
      
      {/* Main Content */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              Yoga for Young People
            </h2>
            <div className="prose-content space-y-4 text-neutral-700">
              <p>
                Through movement, breath and imagination, children learn simple tools that support calm, focus and wellbeing — skills they can carry with them throughout their lives.
              </p>
              <p>
                Classes are adapted to suit different age groups and can be arranged for individuals, small groups, or families who want to practice together.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">
              What Children Gain From Yoga
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { icon: Heart, title: "Body Awareness", desc: "Learning to listen to and understand their bodies" },
                { icon: Sparkles, title: "Confidence", desc: "Building self-esteem through movement and achievement" },
                { icon: Users, title: "Emotional Balance", desc: "Simple tools for managing feelings and stress" },
                { icon: Check, title: "Focus & Calm", desc: "Skills that support learning and daily life" },
              ].map((item, i) => (
                <div key={i} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                  <item.icon className="w-6 h-6 text-gold mb-3" />
                  <h3 className="font-heading text-lg text-neutral-800 mb-2">{item.title}</h3>
                  <p className="text-neutral-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              Interested in Children's Yoga?
            </h2>
            <p className="text-neutral-600 mb-6 max-w-xl mx-auto">
              Get in touch to discuss your child's needs and find out about available sessions.
            </p>
            <Link to="/contact" className="btn-primary" data-testid="bottom-cta">
              Get in Touch <ArrowRight className="w-4 h-4 inline ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Related */}
      <section className="py-12 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-neutral-600 text-sm mb-4">Also available:</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/yoga-classes/sen-yoga" className="text-sm text-gold hover:underline">SEN Yoga</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/yoga-classes/private-yoga" className="text-sm text-gold hover:underline">Private Sessions</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/yoga-classes" className="text-sm text-gold hover:underline">Adult Yoga Classes</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ChildrenFamilyYogaFrome;
